import { useEffect, useMemo, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Skeleton from "@mui/material/Skeleton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import AddIcon from "@mui/icons-material/Add";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BlockIcon from "@mui/icons-material/Block";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import LinkIcon from "@mui/icons-material/Link";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SendIcon from "@mui/icons-material/Send";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import {
  applyAccessChanges, deactivateUser, getAccessUser, getInviteLink, reactivateUser, resendInvite, revokeInvite,
} from "~/services/admin/access";
import { copyText } from "~/utils/clipboard";
import { getApiErrorCode } from "~/utils/apiError";
import AppDrawer from "~/shared/ui/AppDrawer";
import ConfirmDialog from "~/shared/ui/ConfirmDialog";
import { useNotify } from "~/shared/ui/feedback/SnackbarProvider";
import { COLORS } from "~/ui/tokens";
import { LoadErrorBanner, PersonAvatar } from "../shared/StatePanels";
import { ExternalChip, StatusChip } from "./chips";
import GrantsSection from "./GrantsSection";
import InactiveChip from "~/shared/ui/InactiveChip";
import {
  accessErrorMessage, auditActionLabel, daysUntil, formatAuditChange, inviteEmailErrorLabel, formatDateBR, formatDateTimeBR, formatLastSeen,
  formatWhen, ROLE_CARDS, userStatus,
} from "./labels";

const FORBIDDEN_MESSAGE = "Seu acesso mudou enquanto você editava e não permite mais esta ação. Nada foi gravado; recarregamos suas permissões.";
// Erros do POST access/changes que recarregam a ficha (P1-10).
const RELOAD_CODES = ["LAST_ADMIN", "SELF_CHANGE_FORBIDDEN", "USER_DISABLED", "USER_NOT_FOUND", "COMPANY_NOT_FOUND"];

// Bloco "Convite" de quem não está mais como convidado (texto sem mockup para revoked/superseded: Apêndice).
const INVITE_STATUS_LABELS = {
  accepted: { label: "Aceito", color: "#2e7d32" },
  revoked: { label: "Cancelado", color: "#d32f2f" },
  superseded: { label: "Substituído", color: "#546e7a" },
};

const roleKey = (role) => role ?? "none";
const fromRoleKey = (key) => (key === "none" ? null : key);
const sameIds = (a, b) => a.length === b.length && a.every((id) => b.includes(id));

function Section({ icon: Icon, title, children, label, extra }) {
  return (
    <Box component="section" aria-label={label || title} sx={{ mb: 3 }}>
      <Typography sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 12, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: COLORS.muted, mb: 1 }}>
        <Icon sx={{ fontSize: 16 }} aria-hidden /> {title} {extra}
      </Typography>
      {children}
    </Box>
  );
}

export default function PersonDrawer({ userId, open, onClose, onChanged, companies = [], activeAdminsCount = 0, activeAdmins = [], onOpenAudit, onForbidden }) {
  const notify = useNotify();
  const [state, setState] = useState({ status: "loading", detail: null });
  const [attempt, setAttempt] = useState(0);
  const [draftRole, setDraftRole] = useState(undefined);
  const [draftCompanyIds, setDraftCompanyIds] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [manualLink, setManualLink] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [confirmOff, setConfirmOff] = useState(false);
  const [busy, setBusy] = useState(false);
  // Sequência das leituras da ficha: só a resposta mais recente é aplicada (duas concessões rápidas
  // disparam duas releituras silenciosas que podem voltar fora de ordem).
  const readSeq = useRef(0);

  // A ficha fica montada entre pessoas: trocar de pessoa (ou fechar/abrir) não carrega erro, diálogo,
  // menu ou link manual da anterior. O reload da MESMA pessoa (attempt) mantém a mensagem.
  useEffect(() => {
    setError(null);
    setConfirmOff(false);
    setMenuAnchor(null);
    setManualLink(null);
  }, [open, userId]);

  useEffect(() => {
    if (!open || !userId) return undefined;
    let alive = true;
    readSeq.current += 1; // invalida releitura silenciosa em voo
    setState({ status: "loading", detail: null });
    getAccessUser(userId).then(
      (detail) => {
        if (!alive) return;
        setState({ status: "ok", detail });
        setDraftRole(undefined);
        setDraftCompanyIds(null);
        setManualLink(null);
      },
      () => alive && setState({ status: "error", detail: null })
    );
    return () => {
      alive = false;
    };
  }, [open, userId, attempt]);

  const reload = () => setAttempt((n) => n + 1);

  // Concessão gravada: o BE toca users.updated_at, então a ficha guarda o updatedAt novo na hora (o próximo
  // "Salvar alterações" manda expectedUpdatedAt) e relê em silêncio, sem skeleton e sem perder o rascunho,
  // para o histórico mostrar a mudança. onChanged recarrega a matriz, que também manda updatedAt nas alterações.
  const onGrantsSaved = ({ grants, updatedAt }) => {
    const id = state.detail?.id;
    setState((s) => (s.detail?.id === id ? { ...s, detail: { ...s.detail, grants, updatedAt: updatedAt || s.detail.updatedAt } } : s));
    onChanged?.();
    readSeq.current += 1;
    const seq = readSeq.current;
    getAccessUser(id).then(
      (fresh) => {
        if (seq !== readSeq.current) return;
        // defesa extra: nunca volta para um updatedAt mais antigo que o já aplicado
        setState((s) => (s.status === "ok" && s.detail?.id === fresh?.id && !(fresh.updatedAt < s.detail.updatedAt) ? { status: "ok", detail: fresh } : s));
      },
      () => {}
    );
  };
  const detail = state.detail;
  const originalIds = useMemo(() => (detail?.companies || []).map((c) => c.id), [detail]);
  const role = draftRole === undefined ? detail?.role ?? null : draftRole;
  const companyIds = draftCompanyIds ?? originalIds;
  const roleChanged = detail && draftRole !== undefined && (draftRole ?? null) !== (detail.role ?? null);
  const companiesChanged = detail && !sameIds(companyIds, originalIds);
  const dirty = Boolean(roleChanged || companiesChanged);
  const companyName = (id) => companies.find((c) => c.id === id)?.name || detail?.companies.find((c) => c.id === id)?.name || `#${id}`;
  // Nomes das empresas para o histórico: lista da matriz (todas) + as da própria ficha.
  const companiesById = useMemo(
    () => new Map([...(detail?.companies || []), ...companies].map((c) => [c.id, c.name])),
    [companies, detail]
  );
  const isSelf = Boolean(detail?.isSelf);
  const isDisabled = detail?.status === "disabled";
  // activeAdminsCount = Admins ativos na base inteira (GET access/users, spec §6.2) — não depende da página da matriz.
  const lastAdmin = detail?.role === "admin" && detail?.status === "active" && activeAdminsCount === 1;

  const discard = () => {
    setDraftRole(undefined);
    setDraftCompanyIds(null);
    setError(null);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    const change = {
      userId: detail.id,
      expectedUpdatedAt: detail.updatedAt,
      ...(roleChanged ? { role } : {}),
      addCompanyIds: companyIds.filter((id) => !originalIds.includes(id)),
      removeCompanyIds: originalIds.filter((id) => !companyIds.includes(id)),
    };
    try {
      await applyAccessChanges([change]);
      notify({ severity: "success", message: "Alterações salvas." });
      onChanged?.();
      reload();
    } catch (e) {
      const code = getApiErrorCode(e);
      if (code === "STALE_ACCESS") {
        setError("Alguém alterou este acesso antes de você. A ficha foi recarregada; refaça a alteração.");
        reload();
      } else if (code === "FORBIDDEN_ROLE") {
        // Só o FORBIDDEN_ROLE (ator perdeu o papel) recarrega o auth/me; outro 403 (ex.: ORIGIN_MISMATCH) é erro comum.
        setError(FORBIDDEN_MESSAGE);
        reload();
        onForbidden?.();
      } else {
        setError(accessErrorMessage(e, "Não foi possível salvar as alterações. Nada foi gravado."));
        if (RELOAD_CODES.includes(code)) reload();
      }
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async () => {
    setError(null);
    try {
      const { inviteUrl, expiresAt } = await getInviteLink(detail.invite.id);
      if (await copyText(inviteUrl)) {
        setManualLink(null);
        notify({ severity: "success", message: `Link do convite copiado. Ele expira em ${formatDateBR(expiresAt)}.` });
      } else {
        setManualLink(inviteUrl);
      }
    } catch (e) {
      setError(accessErrorMessage(e, "Não foi possível gerar o link do convite."));
    }
  };

  const resend = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await resendInvite(detail.invite.id);
      notify({
        severity: "success",
        message: result?.invite?.emailStatus === "failed"
          ? ["Convite renovado, mas o e-mail falhou — use Copiar link.", inviteEmailErrorLabel(result.invite.emailError)].filter(Boolean).join(" ")
          : "Convite reenviado. O link anterior deixou de valer.",
      });
      onChanged?.();
      reload();
    } catch (e) {
      setError(accessErrorMessage(e, "Não foi possível reenviar o convite."));
    } finally {
      setBusy(false);
    }
  };

  // O erro fica dentro do ConfirmDialog (errorMessage), que só fecha no sucesso.
  const turnOff = async () => {
    if (detail.status === "invited" && detail.invite) await revokeInvite(detail.invite.id);
    else await deactivateUser(detail.id);
    notify({ severity: "success", message: detail.status === "invited" ? "Convite cancelado." : "Acesso desativado." });
    onChanged?.();
    reload();
  };

  const turnOn = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await reactivateUser(detail.id);
      notify({ severity: "success", message: result?.inviteUrl ? "Acesso reativado. Um novo convite foi enviado." : "Acesso reativado." });
      onChanged?.();
      reload();
    } catch (e) {
      setError(accessErrorMessage(e, "Não foi possível reativar o acesso."));
    } finally {
      setBusy(false);
    }
  };

  const inviteBox = () => {
    const invite = detail.invite;
    if (!invite) {
      return <Typography sx={{ p: 1.5, border: "1px solid #e5e7eb", borderRadius: 1 }}>{detail.role === "admin" ? "Admin inicial — sem convite." : "Sem convite registrado."}</Typography>;
    }
    if (detail.status !== "invited") {
      // Rótulo pelo status do convite mais recente (InviteSummary, spec §6.2), não pelo status da pessoa.
      const inviteLabel = INVITE_STATUS_LABELS[invite.status] || INVITE_STATUS_LABELS.accepted;
      return (
        <Box sx={{ p: 1.5, border: "1px solid #e5e7eb", borderRadius: 1, display: "grid", gridTemplateColumns: "1fr auto", rowGap: 1 }}>
          <Typography sx={{ color: COLORS.muted }}>Status</Typography>
          <Typography sx={{ color: inviteLabel.color, fontWeight: 600 }}>{inviteLabel.label}</Typography>
          <Typography sx={{ color: COLORS.muted }}>Convidada por</Typography>
          <Typography>{invite.invitedBy?.name || "—"}</Typography>
          {invite.acceptedAt && (
            <>
              <Typography sx={{ color: COLORS.muted }}>Aceito em</Typography>
              <Typography>{formatDateTimeBR(invite.acceptedAt)}</Typography>
            </>
          )}
        </Box>
      );
    }
    const days = daysUntil(invite.expiresAt);
    return (
      <Box sx={{ p: 1.5, border: "1px solid #e5e7eb", borderRadius: 1 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto", rowGap: 1, alignItems: "center" }}>
          <Typography sx={{ color: COLORS.muted }}>Status</Typography>
          <StatusChip user={detail} />
          <Typography sx={{ color: COLORS.muted }}>Enviado em</Typography>
          <Typography>{formatDateTimeBR(invite.sentAt)}</Typography>
          <Typography sx={{ color: COLORS.muted }}>Expira em</Typography>
          <Typography>
            <b>{formatDateBR(invite.expiresAt)}</b> {invite.expired ? "(vencido)" : days !== null ? `(${days} ${days === 1 ? "dia" : "dias"})` : ""}
          </Typography>
        </Box>
        {invite.emailStatus === "failed" && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            <span>E-mail falhou — use Copiar link</span>
            {inviteEmailErrorLabel(invite.emailError) && <Box component="span" sx={{ display: "block" }}>{inviteEmailErrorLabel(invite.emailError)}</Box>}
          </Alert>
        )}
        <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
          <LoadingButton size="small" variant="outlined" startIcon={<SendIcon />} loading={busy} onClick={resend}>Reenviar</LoadingButton>
          <Button size="small" variant="outlined" startIcon={<LinkIcon />} disabled={invite.expired} onClick={copyLink}>Copiar link</Button>
        </Box>
        <Typography variant="caption" component="p" sx={{ color: COLORS.muted, mt: 1 }}>Reenviar gera um novo link e invalida o anterior.</Typography>
        {manualLink && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2">Copie o link abaixo:</Typography>
            <TextField size="small" fullWidth value={manualLink} InputProps={{ readOnly: true }} onFocus={(e) => e.target.select()} />
          </Box>
        )}
      </Box>
    );
  };

  const adminRule = () => {
    if (detail.role !== "admin") return null;
    // Nomes só quando a matriz tem todos os Admins carregados; senão, só a contagem (lista paginada, P1-5).
    const names = activeAdmins.length === activeAdminsCount ? activeAdmins.map((a) => (a.id === detail.id && isSelf ? "você" : a.name)) : [];
    const list = names.length > 1 ? `${names.slice(0, -1).join(", ")} e ${names[names.length - 1]}` : names[0] || "";
    const plural = activeAdminsCount === 1 ? "1 Admin ativo" : `${activeAdminsCount} Admins ativos`;
    return (
      <Alert severity="info" icon={<ShieldOutlinedIcon />} data-testid="admin-rule" sx={{ mt: 2 }}>
        Há <b>{plural}</b>{list ? ` (${list})` : ""}. Não é possível remover o papel nem desativar o <b>último Admin ativo</b>.
        {isSelf && " Você também não pode desativar o próprio acesso."}
      </Alert>
    );
  };

  const content = () => {
    if (state.status === "loading") {
      return (
        <Box sx={{ py: 2 }}>
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} data-testid="person-skeleton" height={i === 0 ? 72 : 120} sx={{ mb: 1 }} />)}
        </Box>
      );
    }
    if (state.status === "error") {
      return <Box sx={{ py: 2 }}><LoadErrorBanner message="Não foi possível carregar a ficha. Tente novamente em instantes." onRetry={reload} /></Box>;
    }
    const status = userStatus(detail);
    // Empresa inativa não entra no "Adicionar empresa" (vínculo novo → 422 COMPANY_INACTIVE); a já vinculada fica marcada (Q7).
    const available = companies.filter((c) => !companyIds.includes(c.id) && c.active !== false);
    const isInactiveCompany = (id) => [...companies, ...(detail?.companies || [])].some((c) => c.id === id && c.active === false);
    const roleLocked = isSelf || isDisabled;
    return (
      <>
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", py: 2, borderBottom: "1px solid #e5e7eb" }}>
          <PersonAvatar name={detail.name} email={detail.email} size={56} />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {/* Nome (ou e-mail) no título do AppDrawer e e-mail no subtítulo; aqui só "(você)" e o chip de externo. */}
            {(isSelf || detail.isExternal) && (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", mb: 0.5 }}>
                {isSelf && <Typography component="span" sx={{ color: COLORS.muted }}>(você)</Typography>}
                {detail.isExternal && <ExternalChip />}
              </Box>
            )}
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              <StatusChip user={detail} />
              <Typography variant="body2" sx={{ color: COLORS.muted }}>{`Último acesso: ${formatLastSeen(detail.lastAccessAt)}`}</Typography>
            </Box>
          </Box>
          {isDisabled ? (
            <LoadingButton variant="outlined" loading={busy} onClick={turnOn}>Reativar acesso</LoadingButton>
          ) : (
            <Button variant="outlined" color="error" startIcon={<BlockIcon />} disabled={isSelf || lastAdmin} onClick={() => setConfirmOff(true)}>
              {isSelf ? "Desativar" : "Desativar acesso"}
            </Button>
          )}
        </Box>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {status.key === "expired" && <Alert severity="warning" sx={{ mt: 2 }}>Convite expirado — use Reenviar para gerar um novo link.</Alert>}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.2fr 1fr" }, gap: 3, py: 2 }}>
          <Box>
            <Section
              icon={KeyOutlinedIcon}
              title="Acesso Antecipe"
              extra={isSelf && (
                <Tooltip title="Você não pode alterar o próprio papel">
                  <span data-testid="self-role-lock" style={{ display: "inline-flex" }}><LockOutlinedIcon sx={{ fontSize: 15 }} /></span>
                </Tooltip>
              )}
            >
              <RadioGroup value={roleKey(role)} onChange={(e) => setDraftRole(fromRoleKey(e.target.value))} sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                {ROLE_CARDS.map((card) => {
                  const selected = roleKey(card.value) === roleKey(role);
                  return (
                    <FormControlLabel
                      key={card.label}
                      value={roleKey(card.value)}
                      disabled={roleLocked}
                      control={<Radio size="small" />}
                      label={
                        <Box>
                          <Typography sx={{ fontWeight: 600 }}>{card.label}</Typography>
                          <Typography variant="caption" sx={{ color: COLORS.muted }}>{card.description}</Typography>
                        </Box>
                      }
                      sx={{ m: 0, alignItems: "flex-start", p: 1.25, border: `${selected ? 2 : 1}px solid ${selected ? COLORS.accent : "#e5e7eb"}`, borderRadius: 1, bgcolor: selected ? "#f0fafd" : "transparent" }}
                    />
                  );
                })}
              </RadioGroup>
            </Section>
            <Section icon={WorkspacePremiumOutlinedIcon} title="Concessões">
              <GrantsSection
                user={{ id: detail.id, role: detail.role ?? null, grants: detail.grants || [] }}
                isAdminTarget={detail.role === "admin"}
                canEdit={!isDisabled}
                onSaved={onGrantsSaved}
                onForbidden={onForbidden}
              />
            </Section>
            <Section icon={ApartmentOutlinedIcon} title="Empresas">
              <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 1 }}>
                {companyIds.length === 0 && <Typography sx={{ p: 1.5, color: COLORS.muted }}>Não gerencia nenhuma empresa.</Typography>}
                {companyIds.map((id) => (
                  <Box key={id} sx={{ display: "flex", alignItems: "center", gap: 1, p: 1.25, borderBottom: "1px solid #f1f5f9" }}>
                    <ApartmentOutlinedIcon sx={{ color: COLORS.muted, fontSize: 18 }} aria-hidden />
                    <Typography sx={{ fontWeight: 500 }}>{companyName(id)}</Typography>
                    {isInactiveCompany(id) && <InactiveChip sx={{ ml: 0.5 }} />}
                    <Chip size="small" label="Gestor" sx={{ bgcolor: "#e0f5fb", color: "#0277bd", height: 20, fontSize: 11 }} />
                    <Box sx={{ flexGrow: 1 }} />
                    {!isDisabled && (
                      <IconButton size="small" aria-label={`Remover ${companyName(id)}`} onClick={() => setDraftCompanyIds(companyIds.filter((x) => x !== id))}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}
                {!isDisabled && (
                  <Button startIcon={<AddIcon />} disabled={available.length === 0} onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ m: 1 }}>
                    Adicionar empresa
                  </Button>
                )}
                <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                  {available.map((c) => (
                    <MenuItem key={c.id} onClick={() => { setDraftCompanyIds([...companyIds, c.id]); setMenuAnchor(null); }}>{c.name}</MenuItem>
                  ))}
                </Menu>
              </Box>
            </Section>
          </Box>
          <Box>
            <Section icon={MailOutlineIcon} title="Convite" label="Convite">{inviteBox()}</Section>
            <Section icon={HistoryIcon} title="Histórico desta pessoa">
              {(detail.history || []).length === 0 && <Typography variant="body2" sx={{ color: COLORS.muted }}>Nada registrado ainda.</Typography>}
              {(detail.history || []).map((item) => {
                // O alvo (targetLabel) é a própria pessoa e não se repete; entityType "user" (spec §6.2).
                const change = formatAuditChange({ ...item, entityType: item.entityType || "user" }, { companiesById });
                return (
                  <Box key={item.id} sx={{ display: "flex", gap: 1.25, mb: 1.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#7e57c2", mt: 0.75, flexShrink: 0 }} />
                    <Box>
                      <Typography variant="body2">
                        {auditActionLabel(item.action, item)}
                        {(item.before || item.after) && `: ${change.before} → ${change.after}`}
                      </Typography>
                      <Typography variant="caption" sx={{ color: COLORS.muted }}>{`${formatWhen(item.createdAt)} · por ${item.actor?.name || "Sistema"}`}</Typography>
                    </Box>
                  </Box>
                );
              })}
              <Button endIcon={<ArrowForwardIcon />} onClick={() => onOpenAudit?.({ id: detail.id, name: detail.name || detail.email })} sx={{ px: 0 }}>Ver tudo na auditoria</Button>
            </Section>
          </Box>
        </Box>
        {adminRule()}
        <ConfirmDialog
          open={confirmOff}
          onClose={() => setConfirmOff(false)}
          destructive
          title={detail.status === "invited" ? `Cancelar o convite de ${detail.name || detail.email}?` : `Desativar acesso de ${detail.name || detail.email}?`}
          context={detail.email}
          message={detail.status === "invited" ? "O link do convite deixa de funcionar e a pessoa aparece como desativada." : "A pessoa perde o acesso na próxima ação que fizer no Antecipe. Dá para reativar depois."}
          confirmLabel={detail.status === "invited" ? "Cancelar convite" : "Desativar"}
          cancelLabel="Voltar"
          onConfirm={turnOff}
          errorMessage={(e) => accessErrorMessage(e, "Não foi possível desativar o acesso.")}
        />
      </>
    );
  };

  return (
    <AppDrawer
      open={open}
      onClose={onClose}
      width="md"
      closeLabel="Fechar ficha"
      title={detail ? detail.name || detail.email : "Ficha da pessoa"}
      context={detail?.name ? detail.email : undefined}
      footerLeft={
        detail ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: COLORS.muted }} aria-hidden />
            <Typography variant="body2" sx={{ color: COLORS.muted }}>Alterações valem no próximo clique da pessoa (papel relido a cada requisição).</Typography>
          </Box>
        ) : undefined
      }
      actions={
        detail ? (
          <>
            <Button onClick={discard} disabled={!dirty || saving} sx={{ color: "text.primary" }}>Descartar</Button>
            <LoadingButton variant="contained" loading={saving} disabled={!dirty} onClick={save}>Salvar alterações</LoadingButton>
          </>
        ) : undefined
      }
    >
      {content()}
    </AppDrawer>
  );
}
